import React, { useEffect, useMemo, useState } from 'react';
import { PromiseState } from 'react-refetch';
import apiConnect from '../../helpers/apiConnect';
import Box from 'components/elements/Box';
import { ReactComponent as OfficialCharts } from 'icons/official-charts-button-icon.svg';

import EntityCompareLegend from 'components/Header/TopBar/CompareLegend/EntityCompareLegend/EntityCompareLegend';
import CountryCompareLegend from 'components/Header/TopBar/CompareLegend/CountryCompareLegend/CountryCompareLegend';
import colors from 'styles/themes/colors';
import { useTopicFilters } from './utils/useTopicFilters';
import useFilterNames from './utils/useFilterNames';
import useViewSettingsNames from './utils/useViewSettingsNames';
import { AllTopicTypes } from 'topics';
import scrollToTop from 'helpers/scrollToTop';
import useMedia from 'hooks/useMedia';
import { logToAmplitude } from 'helpers/logAmplitudeEvent';
import { useFilterUrlHandlers } from './utils/useFilterUrlHandlers';
import { filtersForTopic } from './shared';
import {
  EntityTypeTitle,
  Title,
  Caret,
  ArtworkPositioner,
  InfoWrapper,
  DSPChartToolbarIcon,
  LogoWrapper,
  TextWrapper,
  ReleaseTypeWrapper,
  BlockWrapper,
  VerticalLine,
  GorizontalLine,
  SmallScreenHeaderWrapper,
  SmallScreenBodyWrapper,
  ArtistsWrapper,
  MenuWrapper,
  IconClear,
} from './TopicFilters.styles';
import { EntityTypes } from 'context/entityContext';
import Artwork from 'components/base/Artwork/Artwork';
import { IChartInfoProps } from 'pages/Charts';
import { LinkWithSearchParams as Link } from 'components/base/Link/Link';
import ViewRelatedMenu from './ViewRelatedMenu/ViewRelatedMenu';
import getTotalUrl from '../../url-constructors/getTotalUrl';
import {
  IFetchTotalsParams,
  ITotalsSource,
} from '../data-containers/BrowseEntities/shared/types';
import {useSelector} from "react-redux";
import {RootState} from "../../redux/rootReducer";

const dataSources = (): ITotalsSource => ({
  fetchTrackVersions: (
    params: IFetchTotalsParams
  ): { trackTotalFetch: string } => ({
    trackTotalFetch: getTotalUrl(params),
  }),
});

const getTrackImage: React.FC<{
  id: string;
  isSmallScreen: boolean;
  thumbnailUrl?: string;
}> = ({ id = '', isSmallScreen, thumbnailUrl = '' }) => {
  return (
    <Box mr={4}>
      <ArtworkPositioner isSmallScreen={isSmallScreen}>
        <Artwork
          entityId={id}
          entityType="track"
          src={thumbnailUrl}
          isVersion={false}
        />
      </ArtworkPositioner>
    </Box>
  );
};

interface ITopicFiltersTopBarProps {
  topic?: AllTopicTypes[];
  entityType?: EntityTypes | string;
  entityIds?: string[];
  entityTitle?: string;
  artworkSrc?: string;
  chartInfo?: IChartInfoProps;
  productType?: string;
  artists?: [{ artist: any; data: any }];
  entitiesById?: any;
  countryIds?: any;
  compareMode?: 'entity' | 'country';
  artistsFulfilled?: boolean;
  isViewRelatedMenuShown?: boolean;

  // react-refetch items
  trackTotalFetch: PromiseState;
  fetchTrackVersions: (args: IFetchTotalsParams) => any;
}

const TopicFiltersTopBar: React.FC<ITopicFiltersTopBarProps> = (props) => {
  const [totalVersions, setTotalVersions] = useState<number>();

  const {
    topic,
    entityIds,
    entityType,
    entityTitle,
    artworkSrc,
    chartInfo,
    productType,
    artists,
    countryIds,
    compareMode,
    artistsFulfilled,
    isViewRelatedMenuShown,

    // react-refetch items
    trackTotalFetch = PromiseState.create<any>(),
    fetchTrackVersions,
  } = props;

  const entitiesById = useSelector((state: RootState) => state.entity.byId);

  const isArtistShown =
    (entityType === 'track' ||
      entityType === 'project' ||
      entityType === 'release') &&
    !compareMode;

  const activeTopic =
    topic && topic.length ? (topic[1] ? topic[1] : topic[0]) : 'home';
  const isLargeScreen = useMedia('(min-width: 50rem)');
  const isSmallScreen = useMedia('(max-width: 40em)');
  const isLessThan1024px = useMedia('(max-width: 1024px)');

  const [filters] = useTopicFilters();

  const artistsDisplayName = useMemo(() => {
    if (artists?.length)
      return artists.map(({ artist }) => artist?.displayName).join(', ');
    return null;
  }, [artists]);

  const currentArtistData = useMemo(() => {
    const artistId = entityType === 'artist' ? entityIds : artists?.[0]?.artist?.id;
    return entitiesById['artist']?.[artistId]?.entity ?? artists?.[0].artist;
  }, [entityType, entityIds, artists])

  const totalTracks = useMemo(() => currentArtistData?.grouped_track_count, [currentArtistData]);
  const totalReleases = useMemo(() => currentArtistData?.grouped_album_count, [currentArtistData]);

  const validFilters = useMemo(() => {
    return activeTopic ? filtersForTopic[activeTopic] || [] : [];
  }, [activeTopic]);
  const filterNames = useFilterNames(filters, validFilters, isLargeScreen);
  const filterLabelText = filterNames.length ? filterNames.join(', ') : '';
  const viewSettingsNames = useViewSettingsNames(activeTopic).join(', ');
  const labelText = [
    ...viewSettingsNames,
    viewSettingsNames.length && filterLabelText.length ? ', ' : '',
    ...filterLabelText,
  ];
  const opacity = labelText.length > 1 ? 1 : 0.67;
  const scrollToTopicbar = (event: any): void => {
    scrollToTop(event, 0);
  };

  const isVersion = false;

  const getLogo = (): any => {
    if (
      entityType === '' ||
      entityType === 'compare' ||
      compareMode ||
      ((!isArtistShown || !artists || !artistsFulfilled) &&
        entityType === 'track')
    ) {
      return null;
    }
    if (entityType === 'Official chart') {
      return (
        <LogoWrapper>
          <OfficialCharts width={38} height={31} />
        </LogoWrapper>
      );
    }
    if (entityType === 'DSP chart') {
      return (
        <LogoWrapper>
          <DSPChartToolbarIcon />
        </LogoWrapper>
      );
    }

    if (isArtistShown && artists) {
      return artists.map(({ artist }) => (
        <ArtworkPositioner
          key={`entity-artwork-${artist?.id}}`}
          isSmallScreen={isSmallScreen}
        >
          <Artwork
            entityId={artist?.id}
            entityType={'artist'}
            src={artist?.thumbnailUrl}
            isVersion={isVersion}
          />
        </ArtworkPositioner>
      ));
    }

    return (
      <ArtworkPositioner isSmallScreen={isSmallScreen}>
        <Artwork
          key={`entity-artwork-${entityIds?.[0]}`}
          entityId={entityIds?.[0]}
          entityType={entityType}
          src={artworkSrc}
          isVersion={isVersion}
        />
      </ArtworkPositioner>
    );
  };

  const getFilters = (): any => {
    let chartFiltersTitle = 'None';

    if (chartInfo?.chartTitle) {
      if (chartInfo?.countryName) {
        chartFiltersTitle = `${chartInfo?.chartTitle}, ${chartInfo?.countryName}`;
      } else {
        chartFiltersTitle = chartInfo?.chartTitle;
      }
    } else if (chartInfo?.countryName) {
      chartFiltersTitle = chartInfo?.countryName;
    }

    return (
      <TextWrapper opacity={opacity}>
        {chartInfo ? (
          `${chartFiltersTitle}`
        ) : (
          <>
            {labelText.length > 1 ? '' : 'None'}
            {labelText.join('')}
          </>
        )}
      </TextWrapper>
    );
  };

  const [, clearFilterUrl] = useFilterUrlHandlers(filters, validFilters);

  const showResetButton = labelText.length > 1 || chartInfo;

  const onResetFilter = (e: any): void => {
    // to not activate scrollTop func
    e.stopPropagation();
    clearFilterUrl();
    logToAmplitude('Filter has been updated', { type: 'Reset all' });
  };

  useEffect(() => {
    const trackId = entityIds?.[0] ?? ''
    const isTrackDataExist = entitiesById['track']?.[trackId]?.loading || entitiesById['track']?.[trackId]?.entity
    const isVersionsTotalNecessary =
      entityTitle !== 'Home' &&
      isViewRelatedMenuShown &&
      entityType === 'track' &&
      !isTrackDataExist &&
      entityIds?.length;

    // eslint-disable-next-line no-console
    console.table({
      isVersionsTotalNecessary,
      entityType,
      entityIds: entityIds?.join(', '),
    });

    if (
      entityType &&
      entityIds &&
      isVersionsTotalNecessary
    ) {
      if (isVersionsTotalNecessary) {
        fetchTrackVersions({
          entityType: 'track',
          entityIds,
        });
      }
    }
  }, [artists]);

  useEffect(() => {
    const trackId = entityIds?.[0]
    if (trackId) {
      const newTotalVersions = entitiesById['track']?.[trackId]?.entity?.group_child_count ||
        trackTotalFetch?.value?.entity?.group_child_count;

      if (!totalVersions && (newTotalVersions || newTotalVersions === 0)) {
        setTotalVersions(newTotalVersions);
      }
    }
  }, [entitiesById, trackTotalFetch]);

  return (
    <>
      {isSmallScreen && entityTitle !== 'Home' && !compareMode ? (
        <>
          <SmallScreenHeaderWrapper>
            <Link
              to={
                entityType !== 'label'
                  ? `/entity/artist/${artists?.[0]?.artist?.id}`
                  : ''
              }
            >
              <Caret />
            </Link>
            {getLogo()}
            <Link
              to={
                entityType !== 'label'
                  ? `/entity/artist/${artists?.[0]?.artist?.id}`
                  : ''
              }
            >
              <Title isSmallScreen underline={entityType !== 'label'}>
                {isArtistShown && artists ? artistsDisplayName : entityTitle}
              </Title>
            </Link>
            <GorizontalLine marginTop="50px" />
          </SmallScreenHeaderWrapper>
          <SmallScreenBodyWrapper>
            {isArtistShown && artists && (
              <BlockWrapper alignItems>
                <EntityTypeTitle>
                  {entityType}
                  {entityType === 'release' && (
                    <ReleaseTypeWrapper>{productType}</ReleaseTypeWrapper>
                  )}
                </EntityTypeTitle>
                <Title entity>{chartInfo?.entityTitle || entityTitle}</Title>
              </BlockWrapper>
            )}
            <Box
              backgroundColor="none"
              opacity={1}
              display="flex"
              marginTop={compareMode ? '10px' : '2px'}
              color={colors.White}
              flexDirection="row"
              height="inherit"
              alignItems="center"
              position="relative"
              onClick={scrollToTopicbar}
            >
              <EntityTypeTitle isSmallScreen resetButton>
                Applied Filters{' '}
                {showResetButton && (
                  <IconClear isSmallScreen top="3px" onClick={onResetFilter} />
                )}
              </EntityTypeTitle>
              {getFilters()}
            </Box>
          </SmallScreenBodyWrapper>
        </>
      ) : (
        <>{getLogo()}</>
      )}

      <InfoWrapper
        flexDirection={isSmallScreen ? 'column' : 'row'}
        marginLeft={isSmallScreen && entityTitle === 'Home' ? '0' : '1em'}
      >
        {compareMode === 'entity' && (
          <EntityCompareLegend
            entityIds={entityIds}
            entitiesById={entitiesById}
            entityType={entityType}
            isSmallScreen={isSmallScreen}
            isLessThan1024px={isLessThan1024px}
            entityTitle={entityTitle}
            topic={topic}
          />
        )}

        {compareMode === 'country' && (
          <CountryCompareLegend
            countryIds={countryIds}
            isSmallScreen={isSmallScreen}
            isLessThan1024px={isLessThan1024px}
          />
        )}

        {entityTitle === 'Home' ? (
          <>
            {isSmallScreen ? (
              <EntityTypeTitle resetButton alignSelf="baseline">
                Applied Filters
              </EntityTypeTitle>
            ) : (
              <>
                <BlockWrapper alignItems>
                  <EntityTypeTitle resetButton>
                    Applied Filters{' '}
                    {showResetButton && <IconClear onClick={onResetFilter} />}
                  </EntityTypeTitle>
                </BlockWrapper>

                <VerticalLine />
              </>
            )}

            <Box
              color={colors.White}
              alignSelf={isSmallScreen ? 'start' : 'center'}
              onClick={scrollToTopicbar}
            >
              {getFilters()}
            </Box>
            {isSmallScreen && showResetButton && (
              <IconClear isSmallScreen top="24px" onClick={onResetFilter} />
            )}
          </>
        ) : (
          <>
            {isArtistShown &&
              artists &&
              !isSmallScreen &&
              isViewRelatedMenuShown && (
                <>
                  <ArtistsWrapper>
                    <BlockWrapper>
                      <EntityTypeTitle>
                        Artist{artists?.length > 1 && 's'}
                      </EntityTypeTitle>
                      <Title>{artistsDisplayName}</Title>
                    </BlockWrapper>
                    {entityType === 'track' &&
                      artists?.length === 1 &&
                      isLargeScreen && (
                        <MenuWrapper>
                          <ViewRelatedMenu
                            browseEntityType="track"
                            entityType="artist"
                            entityIds={artists?.[0]?.artist?.id}
                            artistsDisplayName={
                              artistsDisplayName || entityTitle
                            }
                            total={totalTracks}
                          />
                          <ViewRelatedMenu
                            browseEntityType="album"
                            entityType="artist"
                            entityIds={artists?.[0]?.artist?.id}
                            artistsDisplayName={
                              artistsDisplayName || entityTitle
                            }
                            total={totalReleases}
                          />
                        </MenuWrapper>
                      )}
                  </ArtistsWrapper>

                  <VerticalLine
                    margin={entityType === 'track' && '0 0.875em 0 1em'}
                  />
                </>
              )}

            {isViewRelatedMenuShown && !isSmallScreen && (
              <>
                <ArtistsWrapper>
                  {entityType === 'track' &&
                    getTrackImage({
                      id: entityIds && entityIds.length > 0 ? entityIds[0] : '',
                      isSmallScreen,
                      thumbnailUrl: artworkSrc,
                    })}
                  <BlockWrapper>
                    <EntityTypeTitle>
                      {entityType}
                      {entityType === 'release' && (
                        <ReleaseTypeWrapper>{productType}</ReleaseTypeWrapper>
                      )}
                    </EntityTypeTitle>
                    <Title>{chartInfo?.entityTitle || entityTitle}</Title>
                  </BlockWrapper>
                  {entityType === 'artist' && entityIds && isLargeScreen && (
                    <MenuWrapper>
                      <ViewRelatedMenu
                        browseEntityType="track"
                        entityType="artist"
                        entityIds={entityIds}
                        artistsDisplayName={artistsDisplayName || entityTitle}
                        total={totalTracks}
                      />
                      <ViewRelatedMenu
                        browseEntityType="album"
                        entityType="artist"
                        entityIds={entityIds}
                        artistsDisplayName={artistsDisplayName || entityTitle}
                        total={totalReleases}
                      />
                    </MenuWrapper>
                  )}
                  {entityType === 'track' &&
                    entityIds?.length &&
                    isLargeScreen && (
                      <MenuWrapper>
                        <ViewRelatedMenu
                          browseEntityType="version"
                          entityType="track"
                          entityIds={entityIds}
                          total={totalVersions}
                        />
                      </MenuWrapper>
                    )}
                </ArtistsWrapper>

                <VerticalLine
                  margin={entityType === 'track' && '0 0.875em 0 1em'}
                />
              </>
            )}
            {(!isSmallScreen || entityTitle === 'Home' || compareMode) && (
              <Box
                backgroundColor="none"
                opacity={1}
                display="flex"
                marginTop="2px"
                color={colors.White}
                flexDirection={isSmallScreen && compareMode ? 'row' : 'column'}
                height="inherit"
                alignItems={isSmallScreen && compareMode ? 'center' : 'start'}
                position="relative"
                onClick={scrollToTopicbar}
              >
                <EntityTypeTitle
                  isSmallScreen={isSmallScreen && compareMode}
                  resetButton
                >
                  Applied Filters{' '}
                  {showResetButton && <IconClear onClick={onResetFilter} />}
                </EntityTypeTitle>
                {getFilters()}
              </Box>
            )}
          </>
        )}
      </InfoWrapper>
    </>
  );
};

export default apiConnect(dataSources as any)(TopicFiltersTopBar as any) as any;
